import backgroundOptionsData from "../BackgroundOptionsData";

export const BACKGROUND_IMAGE_FIT = {
  FILL: "cover",
  SHOW_WHOLE: "contain",
};

export const BACKGROUND_IMAGE_BACKDROP = {
  BLUR: "blur",
};

export const BACKGROUND_IMAGE_ADJUSTMENT_VERSION = 1;
export const DEFAULT_BACKGROUND_IMAGE_POSITION = 50;
export const DEFAULT_BACKGROUND_IMAGE_ZOOM = 1;
export const MIN_FILL_BACKGROUND_IMAGE_ZOOM = 1;
export const MIN_SHOW_WHOLE_BACKGROUND_IMAGE_ZOOM = 0.35;
export const MAX_BACKGROUND_IMAGE_ZOOM = 2.5;

const BUILT_IN_BACKGROUND_IMAGE_PATHS = new Set(
  backgroundOptionsData
    .map((option) => option?.image)
    .filter(Boolean)
);

const clampNumber = (value, min, max, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  return Math.max(min, Math.min(max, numeric));
};

const getBackgroundImageUrlCandidate = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  const trimmed = value.trim();
  const match = trimmed.match(/^url\((.*)\)$/i);
  if (!match) {
    return trimmed;
  }

  return match[1].trim().replace(/^['"]|['"]$/g, "");
};

export const isImageBackgroundValue = (value) =>
  typeof value === "string" &&
  (
    value.startsWith("url(") ||
    value.startsWith("/") ||
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("data:image") ||
    value.includes("firebasestorage")
  );

export const isPatternBackgroundValue = (value) =>
  BUILT_IN_BACKGROUND_IMAGE_PATHS.has(getBackgroundImageUrlCandidate(value));

export const isAdjustableBackgroundImageValue = (value) =>
  isImageBackgroundValue(value) && !isPatternBackgroundValue(value);

export const toBackgroundImageValue = (value) => {
  if (typeof value !== "string") {
    return "";
  }
  return value.startsWith("url(") ? value : `url("${value}")`;
};

export const getBackgroundImageUrl = (value) => {
  if (!isImageBackgroundValue(value)) {
    return "";
  }

  return getBackgroundImageUrlCandidate(value);
};

export const normalizeBackgroundImageFit = (value) =>
  (value === BACKGROUND_IMAGE_FIT.SHOW_WHOLE ? BACKGROUND_IMAGE_FIT.SHOW_WHOLE : BACKGROUND_IMAGE_FIT.FILL);

export const normalizeBackgroundImagePosition = (value) =>
  clampNumber(value, 0, 100, DEFAULT_BACKGROUND_IMAGE_POSITION);

export const normalizeBackgroundImageZoom = (value, fit = BACKGROUND_IMAGE_FIT.FILL) => {
  const minZoom = fit === BACKGROUND_IMAGE_FIT.SHOW_WHOLE
    ? MIN_SHOW_WHOLE_BACKGROUND_IMAGE_ZOOM
    : MIN_FILL_BACKGROUND_IMAGE_ZOOM;
  return clampNumber(value, minZoom, MAX_BACKGROUND_IMAGE_ZOOM, DEFAULT_BACKGROUND_IMAGE_ZOOM);
};

export const normalizeBackgroundImageBackdrop = (value) =>
  (value === BACKGROUND_IMAGE_BACKDROP.BLUR ? BACKGROUND_IMAGE_BACKDROP.BLUR : BACKGROUND_IMAGE_BACKDROP.BLUR);

export const normalizeBackgroundImageAdjustmentVersion = (value) => {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0
    ? numeric
    : BACKGROUND_IMAGE_ADJUSTMENT_VERSION;
};

export const getDefaultBackgroundImageSettings = () => ({
  backgroundImageFit: BACKGROUND_IMAGE_FIT.FILL,
  backgroundImagePositionX: DEFAULT_BACKGROUND_IMAGE_POSITION,
  backgroundImagePositionY: DEFAULT_BACKGROUND_IMAGE_POSITION,
  backgroundImageZoom: DEFAULT_BACKGROUND_IMAGE_ZOOM,
  backgroundImageBackdrop: BACKGROUND_IMAGE_BACKDROP.BLUR,
  backgroundImageAdjustmentVersion: BACKGROUND_IMAGE_ADJUSTMENT_VERSION,
});

export const normalizeBackgroundImageSettings = (styleDefaults = {}) => {
  const fit = normalizeBackgroundImageFit(styleDefaults.backgroundImageFit);
  return {
    backgroundImageFit: fit,
    // X/Y are normalized focal percentages. They intentionally describe the
    // important point in the image rather than a pixel crop, so the same values
    // adapt predictably when the Present area changes size.
    backgroundImagePositionX: normalizeBackgroundImagePosition(styleDefaults.backgroundImagePositionX),
    backgroundImagePositionY: normalizeBackgroundImagePosition(styleDefaults.backgroundImagePositionY),
    backgroundImageZoom: normalizeBackgroundImageZoom(styleDefaults.backgroundImageZoom, fit),
    backgroundImageBackdrop: normalizeBackgroundImageBackdrop(styleDefaults.backgroundImageBackdrop),
    backgroundImageAdjustmentVersion: normalizeBackgroundImageAdjustmentVersion(
      styleDefaults.backgroundImageAdjustmentVersion
    ),
  };
};

export const resolveBackgroundImageRenderModel = (backgroundValue, styleDefaults = {}) => {
  if (!isAdjustableBackgroundImageValue(backgroundValue)) {
    return null;
  }

  const imageUrl = getBackgroundImageUrl(backgroundValue);
  if (!imageUrl) {
    return null;
  }

  const settings = normalizeBackgroundImageSettings(styleDefaults);
  const position = `${settings.backgroundImagePositionX}% ${settings.backgroundImagePositionY}%`;
  const showWholeImage = settings.backgroundImageFit === BACKGROUND_IMAGE_FIT.SHOW_WHOLE;

  return {
    imageUrl,
    ...settings,
    showWholeImage,
    hasBlurredBackdrop: showWholeImage && settings.backgroundImageBackdrop === BACKGROUND_IMAGE_BACKDROP.BLUR,
    foregroundStyle: {
      backgroundImage: `url("${imageUrl}")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: position,
      backgroundSize: showWholeImage ? "contain" : "cover",
      transform: `scale(${settings.backgroundImageZoom})`,
      transformOrigin: position,
    },
    backdropStyle: {
      backgroundImage: `url("${imageUrl}")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundSize: "cover",
    },
  };
};

export const buildBackgroundImageSettingsPayload = (settings = {}) => normalizeBackgroundImageSettings(settings);
